import React, { useCallback, useEffect, useState, useMemo, Fragment } from "react";
import { pagedItems, PAGE_SIZE } from "../utils/adminPaging";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../apiAdmin";
import { formatMoneyByCurrency, currencySymbol } from "../utils/currency";
import { absMediaUrl } from "../utils/mediaUrl";
import AdminLoadFailed from "./AdminLoadFailed";
import StatsPending from "./admin-stats/StatsPending";
import { DISPUTE_SUMMARY_URL, disputeSummaryFrom } from "./admin-stats/api";
import { useAdminSummary } from "../hooks/useAdminSummary";
import { withQuietRetry } from "../utils/adminRetry";

interface DisputeOrder {
  _id: string;
  searchQuery: string;
  amount: number;
  status: string;
  customerEmail?: string;
}

interface DisputeDj {
  _id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  slug?: string;
}

// Mirrors Dispute.evidence in models/Dispute.js. Auto-populated by the
// charge.dispute.created webhook from the linked Order — the admin tab
// surfaces it as the "what we attached" panel so the operator can verify
// at a glance that auto-evidence + auto-actions completed.
interface DisputeEvidenceRequestDetails {
  talentName?: string | null;
  song?: string | null;
  artists?: string[];
  album?: string;
  amount?: number | null;
  totalAmount?: number | null;
  currency?: string;
  requestedAt?: string | null;
  paidAt?: string | null;
}

interface DisputeEvidence {
  requestDetails?: DisputeEvidenceRequestDetails | null;
  acceptedAt?: string | null;
  playedAt?: string | null;
  venueAtPlayed?: string | null;
  termsVersion?: string | null;
  termsAcceptedAt?: string | null;
  customerIp?: string | null;
  customerUserAgent?: string | null;
  receiptUrl?: string | null;
  // Phase 3: talent-uploaded track-history evidence.
  trackHistoryStripeFileId?: string | null;
  trackHistoryFilename?: string | null;
  trackHistoryFileType?: "export" | "screenshot" | null;
  trackHistoryUploadedAt?: string | null;
  djNotes?: string | null;
  submittedToProviderAt?: string | null;
  djNotifiedAt?: string | null;
  djSmsNotifiedAt?: string | null;
  customerNotifiedAt?: string | null;
  lastStripeSyncAt?: string | null;
  // Phase 2.5: true when the talent uploaded their track-history file at
  // fulfillment time (proactive) before this dispute fired. Lets admin
  // distinguish "we had everything from day 1" from "talent rushed to
  // upload after we emailed them."
  proactivelyAttached?: boolean | null;
}

interface Dispute {
  _id: string;
  stripeDisputeId: string;
  stripeChargeId?: string;
  order?: DisputeOrder | null;
  dj?: DisputeDj | null;
  amount: number;
  currency: string;
  reason?: string;
  status: string;
  evidenceDueBy?: string | null;
  customerEmail?: string;
  customerName?: string;
  clientNickname?: string;
  clientInstagram?: string;
  cardLast4?: string;
  createdAt: string;
  evidence?: DisputeEvidence | null;
}

function formatReason(reason?: string): string {
  if (!reason) return "—";
  return reason.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmtDateTime(d?: string | null): string {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function fmtMoneyCents(cents?: number | null, currency?: string): string {
  if (typeof cents !== "number") return "—";
  const code = (currency || "usd").toUpperCase();
  // Use the shared symbol map so every currency renders correctly (not just
  // EUR/USD) — covers the 10+ currencies on the roadmap automatically.
  return `${currencySymbol(currency)}${(cents / 100).toFixed(2)} ${code}`;
}

function truncate(s: string | null | undefined, n: number): string {
  if (!s) return "—";
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

function SectionHeader({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div
      style={{
        fontSize: "0.7rem",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        fontWeight: 700,
        color: "#374151",
        marginBottom: 8,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function FieldCell({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <div
        className="muted-text adm-eyebrow-sm"
      >
        {label}
      </div>
      <div className="adm-semibold" style={{ wordBreak: "break-word" }}>{value || "—"}</div>
    </div>
  );
}

function StatusPill({ label, at, skipReason }: { label: string; at?: string | null; skipReason?: string | null }) {
  const done = !!at;
  const skipped = !done && !!skipReason;
  // Done = green, skipped = neutral gray, otherwise awaiting = amber so the
  // admin notices auto-action gaps (transient Resend / Stripe failure).
  const dotColor = done ? "#10b981" : skipped ? "#9ca3af" : "#f59e0b";
  const subText = done ? fmtDateTime(at) : skipped ? `Skipped — ${skipReason}` : "Awaiting";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: dotColor, display: "inline-block", flexShrink: 0 }} />
      <div>
        <div className="adm-semibold">{label}</div>
        <div className="muted-text adm-fs-70">{subText}</div>
      </div>
    </div>
  );
}

const STATUS_BADGES: Record<string, { className: string; label: string }> = {
  needs_response: { className: "badge-declined", label: "Needs Response" },
  warning_needs_response: { className: "badge-declined", label: "Needs Response" },
  under_review: { className: "badge-queued", label: "Under Review" },
  warning_under_review: { className: "badge-queued", label: "Under Review" },
  won: { className: "badge-accepted", label: "Won" },
  lost: { className: "badge-declined", label: "Lost" },
  warning_closed: { className: "badge-pending", label: "Warning Closed" },
};

export default function AdminDisputesTab() {
  const navigate = useNavigate();
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [disputesTotal, setDisputesTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [resending, setResending] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);
    withQuietRetry(() => adminApi.get(`/disputes?limit=${PAGE_SIZE}`))
      .then((r) => {
        const { items, total } = pagedItems<Dispute>(r.data);
        setDisputes(items);
        setDisputesTotal(total);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const loadMore = () => {
    setLoadingMore(true);
    setLoadMoreFailed(false);
    withQuietRetry(() => adminApi.get(`/disputes?limit=${PAGE_SIZE}&skip=${disputes.length}`))
      .then((r) => {
        const { items, total } = pagedItems<Dispute>(r.data);
        setDisputes((prev) => [...prev, ...items]);
        setDisputesTotal(total);
      })
      .catch(() => setLoadMoreFailed(true))
      .finally(() => setLoadingMore(false));
  };

  // The KPI cards count EVERY dispute on the server. A server without the
  // summary route (404) gets the old count over the loaded rows below.
  const summary = useAdminSummary(DISPUTE_SUMMARY_URL, disputeSummaryFrom);

  async function resendNotification(disputeId: string) {
    setResending(disputeId);
    try {
      const { data } = await adminApi.post(`/disputes/${disputeId}/resend-notification`);
      const stamp = new Date().toISOString();
      setDisputes((prev) =>
        prev.map((d) =>
          d._id !== disputeId
            ? d
            : {
                ...d,
                evidence: {
                  ...(d.evidence || {}),
                  ...(data.emailSent ? { djNotifiedAt: stamp } : {}),
                  ...(data.smsSent ? { djSmsNotifiedAt: stamp } : {}),
                },
              }
        )
      );
    } catch {
      // Surface as alert — admin tab is internal, no toast infra.
      alert("Resend failed. Check server logs.");
    } finally {
      setResending(null);
    }
  }

  const filtered = useMemo(() => {
    let list = [...disputes];
    if (filterStatus !== "all") {
      list = list.filter((d) => d.status === filterStatus || (filterStatus === "needs_response" && d.status === "warning_needs_response"));
    }
    return list;
  }, [disputes, filterStatus]);

  const stats = useMemo(() => {
    const open = disputes.filter((d) => d.status === "needs_response" || d.status === "warning_needs_response").length;
    const underReview = disputes.filter((d) => d.status === "under_review" || d.status === "warning_under_review").length;
    const won = disputes.filter((d) => d.status === "won").length;
    const lost = disputes.filter((d) => d.status === "lost").length;
    const totalAmount = disputes.reduce((acc, d) => acc + (d.amount || 0), 0);
    // Disputed total, split per currency (amounts are in minor units / cents).
    const totalDisputedByCur: Record<string, number> = {};
    for (const d of disputes) {
      const cur = (d.currency || "usd").toLowerCase();
      totalDisputedByCur[cur] = (totalDisputedByCur[cur] || 0) + (d.amount || 0) / 100;
    }
    return { open, underReview, won, lost, total: disputes.length, totalAmount, totalDisputedByCur };
  }, [disputes]);

  // Server counts when the server has them; the loaded rows' count on a
  // server from before the summary route; nothing (Loading… / Couldn't load)
  // while the server's answer is missing.
  const kpis = useMemo(() => {
    if (summary.data) {
      const s = summary.data;
      const totalDisputedByCur: Record<string, number> = {};
      let totalAmount = 0;
      for (const [cur, cents] of Object.entries(s.amountByCurrency)) {
        totalDisputedByCur[cur] = cents / 100;
        totalAmount += cents;
      }
      return { open: s.needsResponse, underReview: s.underReview, won: s.won, lost: s.lost, total: s.total, totalAmount, totalDisputedByCur };
    }
    return summary.missing ? stats : null;
  }, [summary.data, summary.missing, stats]);

  if (loading) {
    return (
      <section className="fade-in">
        <p className="muted-text adm-center adm-p-40">Loading disputes...</p>
      </section>
    );
  }

  if (failed && disputes.length === 0) {
    return (
      <section className="fade-in">
        <AdminLoadFailed what="disputes" onRetry={load} />
      </section>
    );
  }

  return (
    <section className="fade-in">
      {/* Stats */}
      {!kpis ? (
        <StatsPending failed={summary.failed} onRetry={summary.retry} />
      ) : (
      <div className="adm-grid-5 adm-mb-14">
        {[
          { label: "Total Disputes", value: String(kpis.total), color: "" },
          { label: "Needs Response", value: String(kpis.open), color: "var(--adm-red)" },
          { label: "Under Review", value: String(kpis.underReview), color: "var(--adm-orange)" },
          { label: "Won", value: String(kpis.won), color: "var(--adm-green)" },
          { label: "Lost", value: String(kpis.lost), color: "var(--adm-red)" },
        ].map((s) => (
          <div key={s.label} className="kpi-card kpi-card-compact">
            <div>
              <div className="kpi-label">{s.label}</div>
              <div className="kpi-value" style={s.color ? { color: s.color } : {}}>{s.value}</div>
            </div>
          </div>
        ))}
      </div>
      )}

      {/* Filter toolbar */}
      <div className="adm-toolbar adm-mb-14">
        <div className="adm-toolbar-group">
          <select className="adm-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="needs_response">Needs Response</option>
            <option value="under_review">Under Review</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
          </select>
        </div>
        <span className="muted-text adm-fs-76">
          {filtered.length} dispute{filtered.length !== 1 ? "s" : ""}
          {kpis && kpis.totalAmount > 0 ? ` · ${formatMoneyByCurrency(kpis.totalDisputedByCur)} total disputed` : ""}
        </span>
      </div>

      {/* Table */}
      <div className="adm-card">
        {disputes.length === 0 ? (
          <p className="muted-text adm-center adm-p-30">
            No disputes found. This is a good thing!
          </p>
        ) : (
          <div className="adm-scrollable-full adm-table-viewport">
            <table className="adm-table-light adm-table-compact sticky-header">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th>Reason</th>
                  <th>Talent</th>
                  <th>Client</th>
                  <th>Evidence Due</th>
                  <th>Order</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => {
                  const badge = STATUS_BADGES[d.status] || { className: "badge-pending", label: d.status };
                  const dj = d.dj;
                  const avatarSrc = absMediaUrl(dj?.avatarUrl);
                  const isDue = d.evidenceDueBy && new Date(d.evidenceDueBy) > new Date();
                  const isOverdue = d.evidenceDueBy && new Date(d.evidenceDueBy) <= new Date();

                  const isExp = expandedId === d._id;
                  return (
                    <Fragment key={d._id}>
                    <tr className="hover-row">
                      <td className="adm-nowrap adm-fs-78">
                        {new Date(d.createdAt).toLocaleDateString()}{" "}
                        <span className="muted-text">{new Date(d.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                      </td>
                      <td>
                        <span className={`badge badge-sm ${badge.className}`}>{badge.label}</span>
                        {d.evidence?.proactivelyAttached && (
                          <span
                            className="badge badge-sm badge-accepted"
                            style={{ marginLeft: 6 }}
                            title="Track-history file was already attached at fulfillment time — passive defense path"
                          >
                            ⚡ Proactive
                          </span>
                        )}
                      </td>
                      <td className="adm-bold">
                        {fmtMoneyCents(d.amount, d.currency)}
                      </td>
                      <td className="adm-fs-78">{formatReason(d.reason)}</td>
                      <td>
                        {dj ? (
                          <div className="dj-cell dj-cell-compact clickable" onClick={() => navigate(`/admin/dj/${dj._id}`)}>
                            {avatarSrc ? (
                              <img src={avatarSrc} className="dj-cell-avatar-sm" alt="" />
                            ) : (
                              <div className="dj-cell-placeholder-sm">{dj.name?.[0]?.toUpperCase() || "?"}</div>
                            )}
                            <span className="dj-link-text adm-fs-82">{dj.name}</span>
                          </div>
                        ) : (
                          <span className="muted-text">Unknown</span>
                        )}
                      </td>
                      <td
                        className="adm-fs-78 adm-pointer"
                        onClick={() => setExpandedId(isExp ? null : d._id)}
                      >
                        {d.customerName || (d.clientInstagram ? `@${d.clientInstagram}` : null) || d.customerEmail || d.clientNickname || (d.cardLast4 ? `•••• ${d.cardLast4}` : null) || "—"}
                        <span className="muted-text adm-ml-4" style={{ fontSize: "0.65rem" }}>{isExp ? "▲" : "▼"}</span>
                      </td>
                      <td className="adm-fs-78 adm-nowrap">
                        {d.evidenceDueBy ? (
                          <span style={{ color: isOverdue ? "var(--adm-red)" : isDue ? "var(--adm-orange)" : undefined, fontWeight: isOverdue ? 700 : undefined }}>
                            {new Date(d.evidenceDueBy).toLocaleDateString()}
                            {isOverdue && " (overdue)"}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="adm-fs-76">
                        {d.order ? (
                          <span className="muted-text" title={d.order.searchQuery}>
                            {d.order.searchQuery?.slice(0, 20) || d.order._id.slice(-6)}
                          </span>
                        ) : (
                          <span className="muted-text">—</span>
                        )}
                      </td>
                    </tr>
                    {isExp && (
                      <tr>
                        <td colSpan={8} className="adm-expand-cell">
                          <div className="adm-fs-78" style={{ padding: "14px 18px" }}>
                            <SectionHeader>Customer</SectionHeader>
                            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                              {[
                                { label: "Real Name", value: d.customerName },
                                { label: "Instagram", value: d.clientInstagram ? `@${d.clientInstagram}` : null },
                                { label: "Email", value: d.customerEmail },
                                { label: "Nickname", value: d.clientNickname },
                                { label: "Card", value: d.cardLast4 ? `•••• ${d.cardLast4}` : null },
                              ].map((item) => (
                                <FieldCell key={item.label} label={item.label} value={item.value} />
                              ))}
                            </div>

                            <SectionHeader style={{ marginTop: 18 }}>Auto-Attached Evidence</SectionHeader>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
                              {[
                                { label: "Song", value: d.evidence?.requestDetails?.song },
                                { label: "Talent", value: d.evidence?.requestDetails?.talentName },
                                { label: "Amount", value: fmtMoneyCents(d.evidence?.requestDetails?.amount ?? null, d.evidence?.requestDetails?.currency) },
                                { label: "Customer paid", value: fmtDateTime(d.evidence?.requestDetails?.paidAt || d.evidence?.requestDetails?.requestedAt) },
                                { label: "Played by talent", value: fmtDateTime(d.evidence?.playedAt) },
                                { label: "Venue at play", value: d.evidence?.venueAtPlayed },
                                { label: "Customer ToS version", value: d.evidence?.termsVersion },
                                { label: "Customer ToS accepted", value: fmtDateTime(d.evidence?.termsAcceptedAt) },
                                { label: "Customer IP", value: d.evidence?.customerIp },
                                { label: "Customer device", value: truncate(d.evidence?.customerUserAgent, 50) },
                              ].map((item) => (
                                <FieldCell key={item.label} label={item.label} value={item.value} />
                              ))}
                            </div>
                            {d.evidence?.receiptUrl ? (
                              <div className="adm-mt-10">
                                <a
                                  href={d.evidence.receiptUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="adm-fs-78 adm-semibold" style={{ color: "#2563eb", textDecoration: "underline" }}
                                >
                                  Open customer receipt ↗
                                </a>
                              </div>
                            ) : null}

                            <SectionHeader style={{ marginTop: 18 }}>Talent-Uploaded Track History</SectionHeader>
                            {d.evidence?.trackHistoryStripeFileId ? (
                              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
                                <FieldCell label="Filename" value={d.evidence.trackHistoryFilename} />
                                <FieldCell
                                  label="File type"
                                  value={
                                    d.evidence.trackHistoryFileType === "export"
                                      ? "Strong: track history export"
                                      : d.evidence.trackHistoryFileType === "screenshot"
                                        ? "Acceptable: screenshot"
                                        : null
                                  }
                                />
                                <FieldCell label="Uploaded" value={fmtDateTime(d.evidence.trackHistoryUploadedAt)} />
                                <FieldCell label="Last Stripe sync" value={fmtDateTime(d.evidence.lastStripeSyncAt)} />
                                {d.evidence.djNotes ? (
                                  <div style={{ gridColumn: "1 / -1" }}>
                                    <FieldCell label="Talent notes" value={d.evidence.djNotes} />
                                  </div>
                                ) : null}
                                <div style={{ gridColumn: "1 / -1" }}>
                                  <a
                                    href={`https://dashboard.stripe.com/disputes/${d.stripeDisputeId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="adm-fs-78 adm-semibold" style={{ color: "#2563eb", textDecoration: "underline" }}
                                  >
                                    View file in Stripe Dashboard ↗
                                  </a>
                                </div>
                              </div>
                            ) : (
                              <div className="muted-text adm-fs-78">
                                Not yet uploaded.
                              </div>
                            )}

                            <SectionHeader style={{ marginTop: 18 }}>Auto-Action Status</SectionHeader>
                            <div style={{ display: "flex", gap: 28, flexWrap: "wrap", alignItems: "center" }}>
                              <StatusPill label="Submitted to Stripe" at={d.evidence?.submittedToProviderAt} />
                              <StatusPill label="Talent notified (email)" at={d.evidence?.djNotifiedAt} />
                              <StatusPill
                                label="Talent notified (SMS)"
                                at={d.evidence?.djSmsNotifiedAt}
                                skipReason={!dj ? "no talent on dispute" : null}
                              />
                              <StatusPill
                                label="Customer notified"
                                at={d.evidence?.customerNotifiedAt}
                                skipReason={!d.customerEmail ? "no email on order" : null}
                              />
                              <button
                                type="button"
                                onClick={() => resendNotification(d._id)}
                                disabled={resending === d._id}
                                style={{
                                  marginLeft: "auto",
                                  background: "transparent",
                                  border: "1px solid #d1d5db",
                                  borderRadius: 8,
                                  padding: "6px 12px",
                                  fontSize: "0.78rem",
                                  fontWeight: 600,
                                  color: "#374151",
                                  cursor: resending === d._id ? "not-allowed" : "pointer",
                                  opacity: resending === d._id ? 0.6 : 1,
                                }}
                              >
                                {resending === d._id ? "Resending…" : "Resend notification"}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
            {disputes.length < disputesTotal && loadMoreFailed && (
              <AdminLoadFailed what="more disputes" onRetry={loadMore} />
            )}
            {disputes.length < disputesTotal && !loadMoreFailed && (
              <button
                className="adm-btn-light adm-center-block"
                disabled={loadingMore}
                onClick={loadMore}
              >
                {loadingMore ? "Loading…" : `Load more (${disputes.length} of ${disputesTotal})`}
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
